import { enumMembers, type ApiSchema } from "./apiSchema";
import { jsonBody, requestJson } from "./http/httpClient";
import { readArray, readNullableString, readObject, readOneOf, readString, type JsonObject } from "./http/jsonDecoding";

/**
 * The signed-in person's own connections to outside services such as GitHub (ADR-0035,
 * built provisionally): connect, disconnect, and what a Cloud import of a repository needs
 * from them. Nothing here ever carries a token; connecting sends the browser to the
 * provider's own page, which sends it back to `/connections/<provider>/callback`.
 */

export type ExternalConnectionState = ApiSchema<"ExternalConnectionState">;
export type ProviderOff = ApiSchema<"ProviderOff">;
export type RemoteRevocation = ApiSchema<"RemoteRevocation">;
export type RepositoryCheckOutcome = ApiSchema<"RepositoryCheckOutcome">;

const CONNECTION_STATES = enumMembers<ExternalConnectionState>({ active: true, needs_reconnect: true, revoked: true });
const PROVIDER_OFF_REASONS = enumMembers<ProviderOff>({ not_configured: true, secret_unreadable: true, vault_off: true });
const REMOTE_REVOCATIONS = enumMembers<RemoteRevocation>({ revoked: true, failed: true, unsupported: true });
const CHECK_OUTCOMES = enumMembers<RepositoryCheckOutcome>({
  public: true,
  ready: true,
  connect: true,
  reconnect: true,
  not_visible: true,
  not_configured: true,
});

export interface ExternalConnection {
  readonly id: string;
  readonly accountLogin: string;
  readonly state: ExternalConnectionState;
  readonly connectedAt: string;
}

export interface ProviderStatus {
  readonly provider: string;
  readonly displayName: string;
  /** Why connecting is not possible; `null` if it is. */
  readonly off: ProviderOff | null;
  readonly connection: ExternalConnection | null;
  /** Where to install DMW's App on repositories; `null` when unknown. */
  readonly installationUrl: string | null;
}

export interface RepositoryCheck {
  readonly outcome: RepositoryCheckOutcome;
  readonly provider: string | null;
  /** The provider as people know it (`GitHub`). */
  readonly providerName: string | null;
  /** The connection to import with, when the outcome is `ready`. */
  readonly connectionId: string | null;
  readonly repository: string | null;
  readonly installationUrl: string | null;
}

const PATH = "/accounts/users/me/external-connections";

function readNullableOneOf<const T extends string>(object: JsonObject, member: string, allowed: readonly T[]): T | null {
  return object[member] === null ? null : readOneOf(object, member, allowed);
}

function decodeConnection(value: unknown): ExternalConnection | null {
  if (value === null) return null;
  const connection = readObject(value, "external connection");
  return {
    id: readString(connection, "id"),
    accountLogin: readString(connection, "account_login"),
    state: readOneOf(connection, "state", CONNECTION_STATES),
    connectedAt: readString(connection, "connected_at"),
  };
}

function decodeStatus(value: unknown): ProviderStatus {
  const status = readObject(value, "provider status");
  return {
    provider: readString(status, "provider"),
    displayName: readString(status, "display_name"),
    off: readNullableOneOf(status, "off", PROVIDER_OFF_REASONS),
    connection: decodeConnection(status["connection"]),
    installationUrl: readNullableString(status, "installation_url"),
  };
}

export function listExternalConnections(signal?: AbortSignal): Promise<ProviderStatus[]> {
  return requestJson({ method: "GET", path: PATH, signal, decodeResponse: (value) => readArray(value, "provider statuses", decodeStatus) });
}

/** Where to send the browser to connect; the provider returns it to `returnTo` afterwards. */
export function beginConnect(provider: string, returnTo: string): Promise<string> {
  const body: ApiSchema<"ConnectBody"> = { return_to: returnTo };
  return requestJson({
    method: "POST",
    path: `${PATH}/${encodeURIComponent(provider)}/authorization`,
    body: jsonBody(body),
    decodeResponse: (value) => readString(readObject(value, "authorization"), "authorization_url"),
  });
}

/** Finish connecting with what the provider sent back; the path to return to. */
export function completeConnect(provider: string, state: string, code: string): Promise<string> {
  const body: ApiSchema<"CompletionBody"> = { state, code };
  return requestJson({
    method: "POST",
    path: `${PATH}/${encodeURIComponent(provider)}/completion`,
    body: jsonBody(body),
    decodeResponse: (value) => readString(readObject(value, "completion"), "return_to"),
  });
}

/** Stops the connection in DMW at once; says whether the provider withdrew it too. */
export function disconnect(connectionId: string): Promise<RemoteRevocation> {
  return requestJson({
    method: "DELETE",
    path: `${PATH}/${encodeURIComponent(connectionId)}`,
    decodeResponse: (value) => readOneOf(readObject(value, "disconnect"), "remote_revocation", REMOTE_REVOCATIONS),
  });
}

export function checkRepository(remote: string, signal?: AbortSignal): Promise<RepositoryCheck> {
  const body: ApiSchema<"RepositoryCheckRequestBody"> = { remote };
  return requestJson({
    method: "POST",
    path: `${PATH}/repository-checks`,
    body: jsonBody(body),
    signal,
    decodeResponse: (value) => {
      const check = readObject(value, "repository check");
      return {
        outcome: readOneOf(check, "outcome", CHECK_OUTCOMES),
        provider: readNullableString(check, "provider"),
        providerName: readNullableString(check, "provider_name"),
        connectionId: readNullableString(check, "connection_id"),
        repository: readNullableString(check, "repository"),
        installationUrl: readNullableString(check, "installation_url"),
      };
    },
  });
}

